import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import type { ILegalDependencyPreviewProps } from '../../../Interfaces/ILegalDependency';
import { renderStatusChip } from './utils';

const renderPreviewEntry = (
  text: string,
  statusLabel?: string | null,
  prefix?: string
) => (
  <Box
    sx={{
      border: theme => `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      p: 1.5,
      bgcolor: theme => theme.palette.background.paper,
    }}
  >
    <Stack alignItems='flex-start' direction='row' spacing={1.25} useFlexGap>
      {prefix ? (
        <Chip
          label={prefix}
          size='small'
          sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
        />
      ) : null}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant='body2'>{text}</Typography>
        {statusLabel ? (
          <Box sx={{ mt: 1 }}>{renderStatusChip(statusLabel)}</Box>
        ) : null}
      </Box>
    </Stack>
  </Box>
);

export const LegalDependencyPreview = ({
  actions,
  conditionGroups,
  getRequirementSummary,
  getStatusLabel,
}: ILegalDependencyPreviewProps) => (
  <Card
    sx={{
      bgcolor: theme => theme.palette.grey[50],
      borderRadius: 3,
    }}
    variant='outlined'
  >
    <CardContent sx={{ p: 2.5 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='subtitle1'>Vorschau</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant='outlined' sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.25, height: '100%' }}>
              <Stack spacing={1.25}>
                <Stack
                  alignItems='center'
                  direction='row'
                  justifyContent='space-between'
                >
                  <Typography variant='subtitle2'>WENN</Typography>
                  <Chip
                    label={`${conditionGroups.length} Grupp${
                      conditionGroups.length === 1 ? 'e' : 'en'
                    }`}
                    size='small'
                    variant='outlined'
                  />
                </Stack>

                {conditionGroups.map((group, groupIndex) => (
                  <Stack key={group.id} spacing={1}>
                    {groupIndex > 0 && group.operator ? (
                      <Chip
                        label={group.operator}
                        size='small'
                        sx={{ alignSelf: 'center', fontWeight: 700 }}
                        variant='outlined'
                      />
                    ) : null}
                    <Box
                      sx={{
                        border: theme => `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: 1.25,
                        bgcolor: theme => theme.palette.background.paper,
                      }}
                    >
                      <Stack spacing={1}>
                        {group.conditions.map((condition, conditionIndex) => (
                          <Box key={condition.id}>
                            {renderPreviewEntry(
                              getRequirementSummary(
                                condition.transactionId,
                                condition.targetType,
                                condition.idLegalRequirement,
                                condition.idLegalPhase,
                                condition.idLegalRequirementState,
                                condition.PhaseIsCompleted
                              ),
                              getStatusLabel(
                                condition.targetType,
                                condition.idLegalRequirementState,
                                condition.PhaseIsCompleted
                              ),
                              conditionIndex > 0 ? 'UND' : undefined
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant='outlined' sx={{ height: '100%', borderRadius: 3 }}>
            <CardContent sx={{ p: 2.25, height: '100%' }}>
              <Stack spacing={1.25}>
                <Stack
                  alignItems='center'
                  direction='row'
                  justifyContent='space-between'
                >
                  <Typography variant='subtitle2'>DANN</Typography>
                  <Chip
                    label={`${actions.length} Aktion${actions.length === 1 ? '' : 'en'}`}
                    size='small'
                    variant='outlined'
                  />
                </Stack>

                {actions.map(action => (
                  <Box key={action.id}>
                    {renderPreviewEntry(
                      getRequirementSummary(
                        action.transactionId,
                        action.targetType,
                        action.idLegalRequirement,
                        action.idLegalPhase,
                        action.targetStateId,
                        action.TargetPhaseIsCompleted
                      ),
                      getStatusLabel(
                        action.targetType,
                        action.targetStateId,
                        action.TargetPhaseIsCompleted
                      )
                    )}
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);
